import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';

const RejectedView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('accessToken');
  const [castingData, setCastingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const fetchCastingData = async () => {
      try {
        const response = await axios.get(
          `https://webback.opencurtainscasting.com/casting/getCasting/${id}`
        );
        setCastingData(response.data.casting);
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
    };

    fetchCastingData();
  }, [id]);

  const handleStatusChangeApprove = async () => {
    setIsSubmitting(true);
    try {
      await axios.patch(
        `https://webback.opencurtainscasting.com/admin/castingAdminApproval/${id}/approved`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSnackbarSeverity('success');
      setSnackbarMessage('Status successfully changed to Approved.');
      setTimeout(() => {
        navigate('/rejected');
      }, 2000);
    } catch (err) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to change status.');
    } finally {
      setSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const handleStatusChangePending = async () => {
    setIsSubmitting(true);
    try {
      await axios.patch(
        `https://webback.opencurtainscasting.com/admin/castingAdminApproval/${id}/pending`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSnackbarSeverity('success');
      setSnackbarMessage('Status successfully changed to Pending.');
      setTimeout(() => {
        navigate('/rejected');
      }, 2000);
    } catch (err) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to change status.');
    } finally {
      setSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color='error'>Error fetching data</Typography>;

  const renderGridItem = (label, value) => (
    <>
      <Grid item xs={2}>
        <Typography variant='body1' sx={{ fontSize: '1rem !important' }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography variant='body1' sx={{ fontSize: '1rem !important' }}>
          :
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Typography variant='body1' sx={{ fontSize: '1rem !important' }}>
          {value || 'N/A'}
        </Typography>
      </Grid>
    </>
  );

const parseEthnicity = (ethnicity) => {
  if (!ethnicity) return 'N/A';

  // If already an array
  if (Array.isArray(ethnicity)) {
    return ethnicity.join(', ');
  }

  // If not a string (object, number, etc.)
  if (typeof ethnicity !== 'string') {
    return String(ethnicity);
  }

  // If stringified JSON array
  const trimmed = ethnicity.trim();
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed.join(', ') : ethnicity;
    } catch (err) {
      console.error('Error parsing ethnicity:', err);
      return ethnicity;
    }
  }

  return ethnicity;
};


  return (
    <Box m='20px' height='80vh' overflow='auto' paddingRight='20px'>
      <Header
        title={`VIEW REJECTED CASTING ID ${id}`}
        subtitle='View Each Rejected Casting Detail'
      />
      {castingData && (
        <Grid container spacing={2}>
          {renderGridItem('Caller Job Title', castingData.callerJobTitle)}
          {renderGridItem('Audition Type', castingData.auditionType)}
          {renderGridItem('Area', castingData.area)}
          {renderGridItem('Category', castingData.category)}
          {renderGridItem('Short Description', castingData.shortDesc)}
          {renderGridItem('Zip Code', castingData.zipCode)}
          {renderGridItem('Expiration Date', castingData.expirationDate)}
          {renderGridItem('Status', castingData.status || 'N/A')}
          {renderGridItem('Company Name', castingData.companyName)}
          {renderGridItem('Admin Status', castingData.adminStatus)}
          {renderGridItem('Amount', castingData.amount)}
          {renderGridItem('Period', castingData.period)}
          {renderGridItem(
            'Created At',
            new Date(castingData.createdAt).toLocaleString()
          )}
          {renderGridItem(
            'Updated At',
            new Date(castingData.updatedAt).toLocaleString()
          )}

          {castingData.roles.map((role, index) => (
            <React.Fragment key={role.id}>
              <Grid item xs={12}>
                <Typography
                  variant='h6'
                  color={'rgb(202, 63, 65)'}
                  sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}
                >
                  Role {index + 1}
                </Typography>
              </Grid>
              {renderGridItem('Casting Role', role.castingRole)}
              {renderGridItem('Casting Type', role.castingType)}
              {renderGridItem('Title', role.title)}
              {renderGridItem('Description', role.description || 'N/A')}
              {renderGridItem('Age Min', role.ageMin)}
              {renderGridItem('Age Max', role.ageMax)}
              {renderGridItem('Ethnicity', parseEthnicity(role.ethinicity))}
              {renderGridItem('Gender', role.gender)}
            </React.Fragment>
          ))}
        </Grid>
      )}

      <Box display='flex' justifyContent='end' m='20px'>
        <Button
          type='submit'
          color='secondary'
          variant='contained'
          style={{ marginRight: '20px' }}
          size='large'
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          onClick={handleStatusChangeApprove}
          sx={{
            borderRadius: '10px',
            backgroundColor: '#ffffff',
            color: '#6e0506',
            border: '3px solid #6e0506',
            fontSize: '0.8rem',
            fontWeight: 'bold',
            padding: '10px 20px',
            '&:hover': {
              backgroundColor: '#6e0506',
              color: '#fff',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#999',
            },
          }}
        >
          <strong>Change Status to Approve</strong>
        </Button>
        {/* <Button
            type="submit"
            color="secondary"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            onClick={handleStatusChangePending}
            sx={{
              borderRadius: "10px",
              backgroundColor: "#ffffff",
              color: "#6e0506",
              border: "3px solid #6e0506",
              fontSize: "0.8rem",
              fontWeight: "bold",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: "#6e0506",
                color: "#fff",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
                color: "#999",
              },
            }}
          >
            <strong>Change Status to Pending</strong>
          </Button> */}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RejectedView;
